import { message } from 'antd';
import { useCallback, useState } from 'react';

import {
  resolveSharePointSharingLink,
  type ResolveSharePointSharingLinkResponseDTO,
} from '@/api/projects/api';

export type { ResolveSharePointSharingLinkResponseDTO } from '@/api/projects/api';

export interface UseSharePointLinkResolverResult {
  resolving: boolean;
  resolveLink: (
    sharingUrl: string,
    onResolved: (resolved: ResolveSharePointSharingLinkResponseDTO | null) => void,
  ) => Promise<void>;
}

/**
 * Manages the async resolve call for a SharePoint/OneDrive sharing link.
 * Draft (input) state stays in the component.
 */
export function useSharePointLinkResolver(
  accessToken: string | null | undefined,
  clientId: string,
  projectId: string,
  microsoftOboAssertion: string | null | undefined,
): UseSharePointLinkResolverResult {
  const [resolving, setResolving] = useState(false);

  const resolveLink = useCallback(
    async (
      sharingUrl: string,
      onResolved: (resolved: ResolveSharePointSharingLinkResponseDTO | null) => void,
    ): Promise<void> => {
      if (!sharingUrl.trim()) {
        void message.warning('Paste a SharePoint or OneDrive sharing link first');
        return;
      }
      if (!accessToken?.trim()) {
        void message.error('Missing client context');
        return;
      }
      setResolving(true);
      try {
        const result = await resolveSharePointSharingLink(
          accessToken.trim(),
          clientId,
          projectId,
          sharingUrl.trim(),
          microsoftOboAssertion,
        );
        onResolved(result);
        void message.success('Sharing link resolved');
      } catch (error) {
        void message.error(
          error instanceof Error ? error.message : 'Failed to resolve sharing link',
        );
      } finally {
        setResolving(false);
      }
    },
    [accessToken, clientId, projectId, microsoftOboAssertion],
  );

  return { resolving, resolveLink };
}
